import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import './RequestAccessPage.css';

const ROLES = ['Agent', 'Broker', 'Buyer', 'Seller', 'Vendor'];

const RequestAccessPage = () => {
  const navigate = useNavigate();

  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [role, setRole]       = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors]   = useState({});
  const [apiError, setApiError] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = 'Full name is required';
    }
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!role) {
      newErrors.role = 'Please select a role';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.contact, {
        name: name.trim(),
        email: email.trim(),
        subject: 'Access Request',
        message: `Role: ${role}\n\nRequested access to Rialty app.`,
      });
      setIsSubmitted(true);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error?.message ||
        'Failed to submit request. Please try again.';
      setApiError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="ra-page">
        <main className="ra-main">
          <div className="ra-card">
            <div className="ra-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h1 className="ra-title">Request Received!</h1>
            <p className="ra-subtitle">We'll be in touch at your email within 24 hours.</p>
            <button className="ra-submit-btn" onClick={() => navigate('/login')}>
              Back to Login
            </button>
          </div>
        </main>
        <footer className="ra-footer">
          <div className="ra-footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="ra-page">
      <main className="ra-main">
        <div className="ra-card">

          {/* Back button */}
          <button className="ra-back-btn" onClick={() => navigate('/login')} type="button">
            <span>←</span>
            <span>Back</span>
          </button>

          {/* Logo */}
          <div className="ra-logo-wrap">
            <div className="ra-logo-circle">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
                <polyline points="9 21 9 12 15 12 15 21"/>
              </svg>
            </div>
          </div>

          <h1 className="ra-title">Request Access</h1>
          <p className="ra-subtitle">Fill out the form below and we'll get back to you shortly.</p>

          {apiError && (
            <div className="ra-api-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="ra-form" noValidate>

            {/* Full Name */}
            <div className={`ra-input-wrap${errors.name ? ' ra-input-wrap--error' : ''}`}>
              <input
                type="text"
                className="ra-input"
                placeholder="Full Name"
                value={name}
                onChange={e => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }}
                disabled={isLoading}
                autoComplete="name"
                autoCapitalize="words"
              />
              <span className="ra-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
            </div>
            {errors.name && <p className="ra-field-error">{errors.name}</p>}

            {/* Email Address */}
            <div className={`ra-input-wrap${errors.email ? ' ra-input-wrap--error' : ''}`}>
              <input
                type="email"
                className="ra-input"
                placeholder="Email Address"
                value={email}
                onChange={e => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: '' })); }}
                disabled={isLoading}
                autoComplete="email"
              />
              <span className="ra-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
            </div>
            {errors.email && <p className="ra-field-error">{errors.email}</p>}

            {/* Role selector */}
            <div className="ra-role-section">
              <p className="ra-role-label">I am a...</p>
              <div className="ra-role-chips">
                {ROLES.map(r => (
                  <button
                    key={r}
                    type="button"
                    className={`ra-role-chip${role === r ? ' ra-role-chip--selected' : ''}`}
                    onClick={() => { setRole(r); setErrors(prev => ({ ...prev, role: '' })); }}
                    disabled={isLoading}
                  >
                    {r}
                  </button>
                ))}
              </div>
              {errors.role && <p className="ra-field-error">{errors.role}</p>}
            </div>

            <button type="submit" className="ra-submit-btn" disabled={isLoading}>
              {isLoading ? <span className="ra-spinner" /> : 'Submit Request'}
            </button>
          </form>

          <p className="ra-alt-link">
            Already have an account? <Link to="/login">Sign In</Link>
          </p>

        </div>
      </main>

      <footer className="ra-footer">
        <div className="ra-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <span>|</span>
          <Link to="/terms">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
};

export default RequestAccessPage;
